/**
 * Quotes and Invoicing are part of Mucka, not skills you install.
 *
 * They were marketplace skills until 16 October 2026, which meant an account
 * could be without them: on that day 464 of 723 production accounts had no
 * `quotes` row and 480 no `invoicing` row, almost all of them free accounts
 * that predate signup installing both (`ALWAYS_INSTALLED_SKILLS`). An owner who
 * signed up to send a quote had to find a shop first (Tom's call: nobody should
 * have to install them).
 *
 * Two halves, and both matter (anti-pattern #29):
 *
 * - **The row.** Migration `20261016_quotes_and_invoicing_are_core` gave every
 *   account both `account_skills` rows, so the ~90 places that read that table
 *   directly stay right without each learning about this file. Signup still
 *   writes them. Nothing removes them: `DELETE /api/skills` and the admin
 *   preview switch both refuse a core slug.
 * - **The grant.** The same migration gave every role the keys it had never
 *   held (`collect_payment` was missing on 78 accounts), never touching a key a
 *   role already has — an owner who switched `view_quotes` off for a role keeps
 *   that decision.
 *
 * `withCoreSkills` is the belt to the migration's braces: the central readers
 * (the sidebar, the engine's skill list, `accountHasSkill`) union these in, so
 * an account created by some path that skipped signup still has them. What a
 * person SEES is then decided by permission alone, which is the point.
 *
 * Pure data, no imports: read by client and server components alike.
 */
export const CORE_SKILL_SLUGS = ['quotes', 'invoicing'] as const;

const CORE = new Set<string>(CORE_SKILL_SLUGS);

export function isCoreSkill(slug: string | null | undefined): boolean {
  return !!slug && CORE.has(slug);
}

/**
 * What Mucka says when asked to install one. A tool RESULT, so it can explain
 * without costing a schema character: the usual reason somebody asks is that
 * they cannot see it, and that is their role, not an install.
 */
export const CORE_SKILL_INSTALL_REPLY =
  'Quotes and invoicing are part of Mucka for every business, so there is nothing to switch on. '
  + 'If someone cannot see them, their role does not include them: an owner can tick them on in Settings, Roles.';

/** The installed slugs plus the core ones, without duplicates, in input order. */
export function withCoreSkills(slugs: readonly string[]): string[] {
  const out = [...slugs];
  for (const slug of CORE_SKILL_SLUGS) if (!out.includes(slug)) out.push(slug);
  return out;
}
